import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, resource } from '@angular/core';
import { DsButtonModule, DsLinkModule, DsMessageModule, DsToastService } from '@bmw-ds/components';

import { AuthenticationService } from '../../auth/authentication/authentication.service';
import { useI18n } from '../i18n/services/i18n.service';
import { BackButtonProviderComponent } from '../navigation/components/back-button/back-button-provider.component';
import { TopSectionComponent } from '../navigation/components/top-section/top-section.component';
import { ReportingDateFilterComponent } from './components/reporting-date-filter/reporting-date-filter.component';
import { ReportingOptionsFilterComponent } from './components/reporting-options-filter/reporting-options-filter.component';
import { FilterCriteria } from './model/reporting.model';
import { ReportingService } from './services/reporting.service';

@Component({
  selector: 'cdf-reporting',
  imports: [
    TopSectionComponent,
    ReportingDateFilterComponent,
    ReportingOptionsFilterComponent,
    DsButtonModule,
    DsMessageModule,
    DsLinkModule,
    BackButtonProviderComponent,
  ],
  template: `
    <cdf-back-button-provider [label]="i18n('reporting.backToCdf')" (back)="backToCdf()" />
    <cdf-top-section>
      @if (loadError()) {
        <ds-message id="loadOptionsError" [messageText]="i18n('reporting.loadOptionsErrorMessage')" tone="critical" />
      } @else {
        <form (submit)="$event.preventDefault()" class="reporting-form">
          <h2>{{ i18n('reporting.header') }}</h2>

          <cdf-reporting-date-filter
            [initialFilterCriteria]="getInitialFilterCriteria()"
            (optionsChanged)="setReportingFilter($event)"
          />
          <cdf-reporting-options-filter
            [countries]="countries()"
            [usecases]="usecases()"
            (optionsChanged)="setReportingFilter($event)"
          />

          <div class="reporting-download">
            <button ds-button id="download-button" (click)="downloadReport()">
              {{ i18n('reporting.download') }}
            </button>
          </div>
        </form>
      }
    </cdf-top-section>
  `,
  styleUrl: './reporting.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportingPage {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly reportingService = inject(ReportingService);
  private readonly dsToastService = inject(DsToastService);
  private readonly location = inject(Location);

  protected readonly i18n = useI18n();

  private readonly filterOptionsResource = resource({
    params: () => this.authenticationService.authHeaders(),
    loader: () => this.reportingService.getAvailableFilterOptions(),
  });

  protected readonly loadError = computed(() => this.filterOptionsResource.error());
  protected readonly countries = computed(() =>
    this.filterOptionsResource.hasValue() ? this.filterOptionsResource.value().countries : [],
  );
  protected readonly usecases = computed(() =>
    this.filterOptionsResource.hasValue() ? this.filterOptionsResource.value().usecases : [],
  );

  protected setReportingFilter(filterCriteria: Partial<FilterCriteria>) {
    this.reportingService.setFilterOptions(filterCriteria);
  }

  protected downloadReport() {
    this.reportingService.downloadReport().subscribe({
      next: (reportingFile) => {
        const downloadUrl = URL.createObjectURL(reportingFile.file);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = reportingFile.filename;
        a.click();
        URL.revokeObjectURL(downloadUrl);
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.status === 422) {
          this.showExportTooBig();
        }
      },
    });
  }

  private showExportTooBig() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('reporting.tooManyResultsErrorTitle'),
      toastText: this.i18n('reporting.tooManyResultsErrorMessage'),
      id: 'REPORT_TOO_BIG_TOAST',
      tone: 'caution',
    });
  }

  protected backToCdf() {
    this.location.assign('/');
  }

  protected getInitialFilterCriteria() {
    return this.reportingService.filter();
  }
}
